import { useCallback, useEffect, useMemo, useState } from 'react';
import { makeStyles, tokens, Text, Spinner } from '@coreai/fluentui-react';
import {
  TriggerHistoryFiltersBar,
  type TimeRangePreset,
} from './TriggerHistoryFiltersBar';
import { TriggerHistoryList } from './TriggerHistoryList';
import { MonitoringListHeader } from '@/components/MonitoringView/shared/MonitoringListHeader';
import { useMonitoringStore } from '@/stores/monitoringStore';
import { useInfiniteTriggerHistories } from '@/hooks/useTriggerHistory';
import type { TriggerHistoryStatus } from '@/types/monitoring';
import { isTriggerHistoryStatusFilterActive } from '@/types/monitoring';
import { useWorkflowTriggers } from '@/hooks/useMonitoringData';

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

const presetToFromIso = (preset: TimeRangePreset): string | undefined => {
  if (preset === 'all') return undefined;
  const now = Date.now();
  switch (preset) {
    case '1h':
      return new Date(now - 1 * MS_PER_HOUR).toISOString();
    case '6h':
      return new Date(now - 6 * MS_PER_HOUR).toISOString();
    case '24h':
      return new Date(now - 24 * MS_PER_HOUR).toISOString();
    case '7d':
      return new Date(now - 7 * MS_PER_DAY).toISOString();
    case '30d':
      return new Date(now - 30 * MS_PER_DAY).toISOString();
    default:
      return undefined;
  }
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
  },
  triggerName: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  noTrigger: {
    padding: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
  loadingTrigger: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalL,
  },
});

export const TriggerHistorySidebar = () => {
  const styles = useStyles();
  const {
    selectedTriggerName,
    selectedTriggerHistoryId,
    triggerHistoryFilters,
    setSelectedTriggerName,
    setSelectedTriggerHistoryId,
    setTriggerHistoryFilters,
    setTriggerHistoryStatusFilter,
    clearTriggerHistoryFilters,
  } = useMonitoringStore();

  const [timeRangePreset, setTimeRangePreset] = useState<TimeRangePreset>('all');

  const handleTimeRangeChange = useCallback(
    (preset: TimeRangePreset) => {
      setTimeRangePreset(preset);
      const from = presetToFromIso(preset);
      setTriggerHistoryFilters({
        startTime: from ? { from } : undefined,
      });
    },
    [setTriggerHistoryFilters]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setTriggerHistoryFilters({
        clientTrackingId: value.trim() ? value : undefined,
      });
    },
    [setTriggerHistoryFilters]
  );

  const handleClear = useCallback(() => {
    setTimeRangePreset('all');
    clearTriggerHistoryFilters();
  }, [clearTriggerHistoryFilters]);

  const statusToggle = useCallback(
    (status: TriggerHistoryStatus) => {
      const next = triggerHistoryFilters.statuses.includes(status)
        ? triggerHistoryFilters.statuses.filter((s) => s !== status)
        : [...triggerHistoryFilters.statuses, status];
      setTriggerHistoryStatusFilter(next);
    },
    [triggerHistoryFilters.statuses, setTriggerHistoryStatusFilter]
  );

  const hasActiveFilters = useMemo(
    () =>
      isTriggerHistoryStatusFilterActive(triggerHistoryFilters.statuses) ||
      Boolean(triggerHistoryFilters.clientTrackingId) ||
      Boolean(triggerHistoryFilters.startTime?.from) ||
      timeRangePreset !== 'all',
    [
      triggerHistoryFilters.statuses,
      triggerHistoryFilters.clientTrackingId,
      triggerHistoryFilters.startTime?.from,
      timeRangePreset,
    ]
  );

  // Resolve which trigger to load. Most Otto workflows have one trigger;
  // when a user has selected one explicitly, honor it.
  const { data: triggers, isLoading: triggersLoading } = useWorkflowTriggers();
  const resolvedTriggerName =
    selectedTriggerName ?? triggers?.[0]?.name ?? null;

  // Auto-bind the first trigger so the rest of the UI has a name to work with.
  useEffect(() => {
    if (!selectedTriggerName && triggers && triggers.length > 0 && triggers[0]) {
      setSelectedTriggerName(triggers[0].name);
    }
  }, [selectedTriggerName, triggers, setSelectedTriggerName]);

  const {
    data: histories,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    isRefetching,
  } = useInfiniteTriggerHistories(resolvedTriggerName);

  const handleSelect = useCallback(
    (name: string) => setSelectedTriggerHistoryId(name),
    [setSelectedTriggerHistoryId]
  );

  const handleLoadMore = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  const handleRefresh = useCallback(() => {
    void refetch();
  }, [refetch]);

  return (
    <div className={styles.container}>
      <TriggerHistoryFiltersBar
        filters={triggerHistoryFilters}
        onStatusToggle={statusToggle}
        onSearchChange={handleSearchChange}
        onTimeRangeChange={handleTimeRangeChange}
        onClear={handleClear}
        timeRangePreset={timeRangePreset}
      />
      {triggersLoading && !resolvedTriggerName ? (
        <div className={styles.loadingTrigger}>
          <Spinner size="tiny" label="Loading triggers..." />
        </div>
      ) : !resolvedTriggerName ? (
        <Text className={styles.noTrigger}>This workflow has no triggers.</Text>
      ) : (
        <>
          <Text className={styles.triggerName}>Trigger: {resolvedTriggerName}</Text>
          <MonitoringListHeader
            count={histories?.length ?? 0}
            unit="evaluation"
            onRefresh={handleRefresh}
            isRefreshing={isRefetching}
          />
          <TriggerHistoryList
            histories={histories ?? []}
            isLoading={isLoading}
            selectedHistoryName={selectedTriggerHistoryId}
            onSelect={handleSelect}
            hasNextPage={Boolean(hasNextPage)}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={handleLoadMore}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={handleClear}
          />
        </>
      )}
    </div>
  );
};
